import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft, CheckCircle2, ChevronLeft, ChevronRight, Clock3, BadgePoundSterling, CalendarCheck2 } from 'lucide-react';
import { api, ApiRequestError } from '../../lib/api';
import { useAuth } from '../../stores/auth';
import { PillButton, PillLink } from '../../components/site/Pill';
import { Field, Input } from '../../components/site/Form';
import { PhoneInput } from '../../components/ui/PhoneInput';
import { EmailCodeSteps } from '../../components/site/EmailCodeSteps';
import { ListingFormPage, AsideFact, useListing } from './ListingFormPage';
import { londonParts, fmtWhen } from '../../lib/dates';
import { todayIso } from '../../lib/applicationForm';
import type { FreeSlot } from '../../lib/viewingSettings';
import { phoneOk, phoneError } from '../../lib/phone';

/**
 * Book a viewing (/rent/:slug/viewing; docs/plans/viewing-first-onboarding.md, phase 3), two steps shown one at a time —
 * first name, last name, email, mobile (Next lights up once they are all in), then one of the flat's free slots — the days that have one (two weeks at a time, "Later" for two more, up to
 * eight) and the free times on the picked day — and the one consent. POST /public/viewings books it (BOOKED, no request
 * to confirm), opens the account by email and emails a 6-digit code. The thank-you page takes the code and a password
 * and signs them in (EmailCodeSteps); an email that already has an account is told to sign in. A slot taken meanwhile
 * (409 SLOT_UNAVAILABLE) says so on the picker and reloads the slots. The application comes after the viewing.
 */

const WEEKS_AT_A_TIME = 2;
const MAX_WEEKS = 8;
const addDaysIso = (day: string, n: number) => { const d = new Date(`${day}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
const dayBits = (day: string) => {
  const d = new Date(`${day}T12:00:00Z`);
  const f = (o: Intl.DateTimeFormatOptions) => d.toLocaleDateString('en-GB', { ...o, timeZone: 'UTC' });
  return { weekday: f({ weekday: 'short' }), date: f({ day: 'numeric' }), month: f({ month: 'short' }) };
};

interface Booked { leadId: string; viewingId: string; scheduledAt: string; account: 'new' | 'existing'; codeSent: boolean }
interface Errors { firstName?: string; lastName?: string; email?: string; mobile?: string; slot?: string; consent?: string }

export default function SiteViewing() {
  const { slug } = useParams<{ slug: string }>();
  const user = useAuth((s) => s.user);
  const { data: l } = useListing(slug);
  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [mobile, setMobile] = useState(user?.phone ?? '');
  const [consent, setConsent] = useState(false);
  const [pickedDay, setDay] = useState<string | null>(null);
  const [pickedSlot, setSlot] = useState<string | null>(null);
  const [weeks, setWeeks] = useState(WEEKS_AT_A_TIME);
  const [errors, setErrors] = useState<Errors>({});
  const [slotGone, setSlotGone] = useState<string | null>(null);
  const [step, setStep] = useState<1 | 2>(1);
  const [touched, setTouched] = useState<{ email?: boolean; mobile?: boolean }>({});
  // A new step starts at the top of the page (on a phone the Next button sits well down the page).
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [step]);
  // Signed in while the page loads: their name, email and mobile go in (still editable).
  useEffect(() => {
    if (!user) return;
    setFirstName((v) => v || user.firstName || ''); setLastName((v) => v || user.lastName || '');
    setEmail((v) => v || user.email || ''); setMobile((v) => v || user.phone || '');
  }, [user]);

  const from = todayIso();
  const to = addDaysIso(from, weeks * 7 - 1);
  const slots = useQuery({
    queryKey: ['public-viewing-slots', slug, to],
    queryFn: async () => (await api.public.get<{ listingId: string; slots: FreeSlot[] }>(`/public/listings/${slug}/viewing-slots`, { from, to })).data.slots,
    enabled: !!slug, placeholderData: keepPreviousData,
  });
  const byDay = useMemo(() => {
    const m = new Map<string, Array<FreeSlot & { time: string }>>();
    for (const s of slots.data ?? []) { const p = londonParts(s.startsAt); m.set(p.day, [...(m.get(p.day) ?? []), { ...s, time: p.time }]); }
    return m;
  }, [slots.data]);
  const days = [...byDay.keys()];
  // The first free day to start with; a day that has filled up gives way to the next; a time that has gone is unpicked.
  const day = pickedDay && byDay.has(pickedDay) ? pickedDay : days[0] ?? null;
  const slot = pickedSlot && (slots.data ?? []).some((s) => s.startsAt === pickedSlot) ? pickedSlot : null;

  const book = useMutation({
    mutationFn: async () => (await api.public.post<Booked>('/public/viewings', {
      listingSlug: slug, firstName: firstName.trim(), lastName: lastName.trim(), email: email.trim(), mobile, slotStartsAt: slot, privacyNoticeAt: new Date().toISOString(),
    })).data,
    onError: (e) => {
      if (e instanceof ApiRequestError && e.code === 'SLOT_UNAVAILABLE') {
        setSlotGone(`${e.message}. The free times have been refreshed.`);
        setSlot(null);
        void slots.refetch();
      }
    },
  });

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const mobileOk = phoneOk(mobile);
  const detailsOk = !!firstName.trim() && !!lastName.trim() && emailOk && mobileOk;
  const whenOk = !!slot && consent;
  // The number's message once the person has left the field and typed something that is not a real number.
  const mobileErr = touched.mobile && mobile && !mobileOk ? phoneError(mobile) : errors.mobile;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (step !== 2 || !whenOk) return;
    setErrors({}); setSlotGone(null);
    book.mutate();
  };
  // A refusal that is not about the slot (a bad mobile, the rate limit): shown above the button.
  const serverError = book.isError && !(book.error instanceof ApiRequestError && book.error.code === 'SLOT_UNAVAILABLE') ? (book.error as Error).message : null;

  if (book.isSuccess) {
    return <ListingFormPage slug={slug} listing={l} title="" done={<BookedThanks booked={book.data} email={email.trim()} headline={l?.headline ?? null} />}>{null}</ListingFormPage>;
  }

  const times = day ? byDay.get(day) ?? [] : [];
  return (
    <ListingFormPage
      listing={l}
      title={step === 1 ? <>Book a viewing</> : null}
      aside={<>
        <AsideFact icon={<Clock3 />}>Viewings take about half an hour</AsideFact>
        <AsideFact icon={<BadgePoundSterling />}>Free, and no fees if you apply later</AsideFact>
        <AsideFact icon={<CalendarCheck2 />}>{slot ? <>Chosen: <span className="font-medium text-ink-900">{fmtWhen(slot)}</span></> : 'Pick a day and a time below'}</AsideFact>
      </>}
    >
      <form onSubmit={submit} noValidate className={step === 1 ? 'lg:mt-12' : ''}>
        {step === 1 && (
          <Step n={1} of={2} title="Your details">
            <div className="grid gap-6 sm:grid-cols-2 sm:gap-x-8">
              <Field label="First name" error={errors.firstName}><Input value={firstName} onChange={(e) => setFirstName(e.target.value)} autoComplete="given-name" maxLength={80} /></Field>
              <Field label="Last name" error={errors.lastName}><Input value={lastName} onChange={(e) => setLastName(e.target.value)} autoComplete="family-name" maxLength={80} /></Field>
              <Field label="Email" error={touched.email && email.trim() && !emailOk ? 'Put in a valid email address' : errors.email}><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} onBlur={() => setTouched((x) => ({ ...x, email: true }))} autoComplete="email" placeholder="you@example.com" /></Field>
              <Field label="Mobile" error={mobileErr}><PhoneInput kit="site" value={mobile} onChange={setMobile} onBlur={() => setTouched((x) => ({ ...x, mobile: true }))} autoComplete="tel" invalid={!!mobileErr} /></Field>
            </div>
            <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
              <PillButton type="button" disabled={!detailsOk} onClick={() => setStep(2)} className="w-full sm:w-auto disabled:cursor-not-allowed disabled:opacity-40">Next</PillButton>
            </div>
          </Step>
        )}

        {step === 2 && (
          <Step n={2} of={2} title="When would you like to come?">
            {slotGone && <p className="site-error mb-5" role="alert">{slotGone}</p>}

            {slots.isLoading ? (
              <div className="flex flex-wrap gap-3">{Array.from({ length: 6 }, (_, i) => <span key={i} className="h-[92px] w-[80px] shrink-0 animate-pulse rounded-[16px] bg-ink-100" />)}</div>
            ) : !days.length ? (
              <div className="rounded-[16px] border border-ink-200 bg-ink-50/50 p-6 text-[15px] leading-relaxed text-ink-600">
                No free viewing times in the next {weeks} weeks.{' '}
                {weeks < MAX_WEEKS
                  ? <button type="button" className="copy-link font-medium" onClick={() => setWeeks((w) => Math.min(MAX_WEEKS, w + WEEKS_AT_A_TIME))}>Look further ahead</button>
                  : <>Please <Link to="/contact" className="copy-link font-medium">get in touch</Link> and we'll find a time.</>}
              </div>
            ) : (
              <>
                <DayCarousel>
                  {days.map((d) => {
                    const b = dayBits(d); const on = d === day;
                    return (
                      <button key={d} type="button" role="radio" aria-checked={on} onClick={() => { setDay(d); setSlot(null); }}
                        className={`flex h-[92px] w-[80px] shrink-0 snap-start flex-col items-center justify-center gap-0.5 rounded-[16px] border transition-colors duration-200 ${on ? 'border-ink-900 bg-ink-900 text-white' : 'border-ink-200 bg-white text-ink-800 hover:border-bronze-400'}`}>
                        <span className={`text-[11px] font-medium uppercase tracking-[0.14em] ${on ? 'text-bronze-300' : 'text-ink-500'}`}>{b.weekday}</span>
                        <span className="font-display text-[28px] leading-none">{b.date}</span>
                        <span className={`text-[12px] ${on ? 'text-white/80' : 'text-ink-500'}`}>{b.month}</span>
                      </button>
                    );
                  })}
                  {weeks < MAX_WEEKS && (
                    <button type="button" onClick={() => setWeeks((w) => Math.min(MAX_WEEKS, w + WEEKS_AT_A_TIME))} disabled={slots.isFetching}
                      className="flex h-[92px] w-[80px] shrink-0 snap-start flex-col items-center justify-center rounded-[16px] border border-dashed border-ink-300 text-[11px] font-medium uppercase tracking-[0.14em] text-ink-600 transition-colors hover:border-bronze-400 hover:text-ink-900 disabled:opacity-50">
                      Later<ChevronRight className="mt-1.5 h-4 w-4" />
                    </button>
                  )}
                </DayCarousel>
                {day && (
                  <div className="mt-5">
                    <div className="flex flex-wrap gap-2.5" role="radiogroup" aria-label="Time">
                      {times.map((t) => {
                        const on = t.startsAt === slot;
                        return (
                          <button key={t.startsAt} type="button" role="radio" aria-checked={on} onClick={() => { setSlot(t.startsAt); setErrors((x) => ({ ...x, slot: undefined })); setSlotGone(null); }}
                            className={`min-h-[48px] min-w-[92px] rounded-full border px-5 text-[15px] font-medium transition-colors duration-200 ${on ? 'border-bronze-500 bg-bronze-500 text-white' : 'border-ink-200 bg-white text-ink-800 hover:border-bronze-400'}`}>
                            {t.time}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}

            <label className="site-check mt-6 !text-[15px] !text-ink-800">
              <input type="checkbox" checked={consent} onChange={(e) => { setConsent(e.target.checked); setErrors((x) => ({ ...x, consent: undefined })); }} />
              <span>I have read the privacy notice and Lotus Living may contact me about this home<span className="text-bronze-500"> *</span></span>
            </label>

            {serverError && <p className="site-error mt-5" role="alert">{serverError}</p>}
            <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-5">
              <button type="button" onClick={() => setStep(1)} className="order-2 inline-flex min-h-[48px] items-center justify-center gap-1.5 text-[14px] font-medium text-ink-600 transition hover:text-ink-900 sm:order-1"><ArrowLeft className="h-4 w-4" /> Back</button>
              <PillButton type="submit" disabled={!whenOk || book.isPending} className="order-1 w-full sm:order-2 sm:w-auto disabled:cursor-not-allowed disabled:opacity-40">{book.isPending ? 'Booking…' : 'Book this viewing'}</PillButton>
              {slot && <p className="order-3 text-[14px] text-ink-500 sm:ml-auto">Chosen: <span className="font-medium text-ink-900">{fmtWhen(slot)}</span></p>}
            </div>
          </Step>
        )}
      </form>
    </ListingFormPage>
  );
}

/**
 * The days in one scrolling row with an arrow either side (arrows only show when there is more that way; on a phone the
 * row swipes). Each arrow moves the row by most of its width.
 */
function DayCarousel({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [edge, setEdge] = useState({ left: false, right: false });
  const measure = () => {
    const el = ref.current; if (!el) return;
    setEdge({ left: el.scrollLeft > 4, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4 });
  };
  useEffect(() => {
    measure();
    const el = ref.current; if (!el) return;
    const ro = new ResizeObserver(measure); ro.observe(el);
    return () => ro.disconnect();
  }, [children]);
  const go = (dir: 1 | -1) => ref.current?.scrollBy({ left: dir * ref.current.clientWidth * 0.8, behavior: 'smooth' });
  const arrow = 'absolute top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-ink-200 bg-white text-ink-700 shadow-card transition hover:border-bronze-400 hover:text-ink-900 sm:flex';
  return (
    <div className="relative">
      {edge.left && <button type="button" aria-label="Earlier days" onClick={() => go(-1)} className={`${arrow} -left-5`}><ChevronLeft className="h-4 w-4" /></button>}
      <div ref={ref} onScroll={measure} role="radiogroup" aria-label="Day"
        className="no-scrollbar -mx-1 flex snap-x gap-3 overflow-x-auto px-1 py-1">
        {children}
      </div>
      {edge.right && <button type="button" aria-label="Later days" onClick={() => go(1)} className={`${arrow} -right-5`}><ChevronRight className="h-4 w-4" /></button>}
    </div>
  );
}

/** One step of the form, shown on its own: "Step n of m", the heading, then the controls. */
function Step({ n, of, title, children }: { n: number; of: number; title: ReactNode; children: ReactNode }) {
  return (
    <section aria-labelledby={`step-${n}`}>
      <p className="eyebrow">Step {n} of {of}</p>
      <h2 id={`step-${n}`} className="h-card mt-3 text-ink-900">{title}</h2>
      <div className="mt-6 sm:mt-7">{children}</div>
    </section>
  );
}

/**
 * Booked: the day and time, then — for a new account — the code emailed with the booking → a password → signed in →
 * "See your viewing"; for an email that already has an account, "Sign in to see your viewing".
 */
function BookedThanks({ booked, email, headline }: { booked: Booked; email: string; headline: string | null }) {
  const user = useAuth((s) => s.user);
  const [signedIn, setSignedIn] = useState(false);
  const when = fmtWhen(booked.scheduledAt);
  const mine = !!user && user.email.toLowerCase() === email.toLowerCase();
  return (
    <div className="rise mx-auto mt-10 max-w-[480px] sm:mt-16">
      <div className="text-center">
        <CheckCircle2 className="mx-auto h-12 w-12 text-bronze-500" />
        <h1 className="h-section mt-6 text-ink-900">Your viewing is booked</h1>
        <p className="mt-4 text-[16px] leading-relaxed text-ink-600">
          Booked for <span className="font-medium text-ink-900">{when}</span>{headline ? <> at <span className="font-medium text-ink-900">{headline}</span></> : ''}.
        </p>
      </div>

      <div className="mt-8 rounded-[18px] border border-ink-200 bg-white p-6 sm:mt-10 sm:p-8">
        {signedIn || mine ? (
          <div className="text-center">
            <p className="text-[15px] leading-relaxed text-ink-600">{signedIn ? "You're signed in. " : ''}Your viewing, and everything after it, is in your account.</p>
            <PillLink to="/app/my-booking" className="mt-6 w-full sm:w-auto">See your viewing</PillLink>
          </div>
        ) : booked.codeSent ? (
          <>
            <p className="eyebrow !text-bronze-600">Set up your account</p>
            <div className="mt-4"><EmailCodeSteps
              start="code" email={email} onSignedIn={() => setSignedIn(true)} submitLabel="Save and see my viewing"
              intro={<>We've emailed a 6-digit code to <span className="font-medium text-ink-900">{email}</span> — enter it to set up your account. It works for 15 minutes.</>}
            /></div>
          </>
        ) : (
          <div className="text-center">
            <p className="text-[15px] leading-relaxed text-ink-600">You already have a Lotus Living account with <span className="font-medium text-ink-900">{email}</span>. We've emailed the details.</p>
            <PillLink to={`/login?next=${encodeURIComponent('/app/my-booking')}`} className="mt-6 w-full sm:w-auto">Sign in to see your viewing</PillLink>
          </div>
        )}
      </div>
    </div>
  );
}
